import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";
/**
 * The brand's `.card`: a `--lg-border` hairline on `--lg-surface` at the 14px
 * radius, `1.7rem 1.6rem` of padding, and a hover that lifts the border one
 * step to `--lg-border-2`.
 *
 * There is deliberately NO shadow. Elevation on this brand is the border ramp
 * plus the surface ramp; the only three shadows in the source page are on
 * panels floating over a moving canvas. Reach for `shadow-float` when
 * something genuinely floats, and not before.
 *
 * `accent` reproduces `.pillar`: the 6% gradient wash on the header, the
 * accent-tinted hover border and the 3px lift. It is for a small set of
 * feature cards, not for a list.
 */
export const cardVariants = cva(["lg:rounded-lg lg:border lg:bg-card lg:transition-all lg:duration-200"], {
    variants: {
        accent: {
            none: "lg:border-hairline lg:hover:border-hairline-2",
            primary: "lg:border-hairline lg:hover:border-primary/45 lg:hover:-translate-y-[3px]",
            violet: "lg:border-hairline lg:hover:border-accent/45 lg:hover:-translate-y-[3px]",
            cyan: "lg:border-hairline lg:hover:border-info/45 lg:hover:-translate-y-[3px]",
        },
        /** Off for a card in a list, where a hover on every row is noise. */
        interactive: { true: "", false: "lg:hover:border-hairline lg:hover:translate-y-0" },
        padded: { true: "lg:px-[1.6rem] lg:py-[1.7rem]", false: "" },
    },
    defaultVariants: { accent: "none", interactive: true, padded: true },
});
export function Card({ className, accent, interactive, padded, as, ...props }) {
    const Component = as ?? "div";
    return (_jsx(Component, { "data-slot": "card", className: cn(cardVariants({ accent, interactive, padded }), className), ...props }));
}
export function CardHeader({ className, ...props }) {
    return (_jsx("div", { "data-slot": "card-header", className: cn("lg:flex lg:flex-col lg:gap-2", className), ...props }));
}
export function CardTitle({ className, as, ...props }) {
    const Component = as ?? "h3";
    return (_jsx(Component, { "data-slot": "card-title", className: cn("lg:text-h3 lg:font-bold lg:text-strong", className), ...props }));
}
export function CardDescription({ className, ...props }) {
    return (_jsx("p", { "data-slot": "card-description", className: cn("lg:text-body-sm lg:text-muted-foreground lg:max-w-none", className), ...props }));
}
export function CardContent({ className, ...props }) {
    return _jsx("div", { "data-slot": "card-content", className: cn("lg:mt-4", className), ...props });
}
export function CardFooter({ className, ...props }) {
    return (_jsx("div", { "data-slot": "card-footer", className: cn("lg:mt-5 lg:flex lg:flex-wrap lg:items-center lg:gap-3", className), ...props }));
}
export const cardIconVariants = cva([
    "lg:mb-4 lg:flex lg:size-9.5 lg:items-center lg:justify-center lg:rounded-sm lg:border",
    "lg:[&_svg]:size-4.5",
], {
    variants: {
        accent: {
            primary: "lg:border-primary/30 lg:bg-primary/[0.14] lg:text-primary",
            violet: "lg:border-accent/30 lg:bg-accent/[0.14] lg:text-accent",
            cyan: "lg:border-info/30 lg:bg-info/[0.14] lg:text-info",
            teal: "lg:border-success/30 lg:bg-success/[0.14] lg:text-success",
        },
    },
    defaultVariants: { accent: "primary" },
});
/**
 * The 38px accent chip from `.card .ic`, at the brand's own tint formula: 14%
 * of the accent for the fill and 32% for the border. Decorative, so it is
 * hidden from assistive technology.
 */
export function CardIcon({ className, accent, ...props }) {
    return (_jsx("div", { "data-slot": "card-icon", "aria-hidden": "true", className: cn(cardIconVariants({ accent }), className), ...props }));
}
//# sourceMappingURL=card.js.map